import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { archivesApi } from '../services/archivesApi';
import './Histoire.css';

// Layout asymétrique : on conserve les 7 mêmes positions de tuile (a1..a7),
// l'API alimente les images dans l'ordre publié.
const TILE_SLOTS = ['a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7'];

/* =================================================================
   Collections d'archives utilisées comme source de chaque section :
   - figures-fondatrices  → Figures
   - jalons-historiques   → Timeline
   - traditions-layenes   → Traditions
   - archives-photo / type=photo → Galerie archives
   ================================================================= */

const FIGURES_FALLBACK = [
  { title: "Seydina Limamou Laye",      author: "Fondateur · 1843 — 1909",  summary: "Né à Yoff, il se retire à Cambérène en 1884 où il fonde la confrérie layène. Sa retraite face à l'océan marque la naissance du village.", imageSeed: "cb-hist-limamou" },
  { title: "Seydina Issa Rouhou Laye",  author: "Successeur · 1876 — 1949", summary: "Fils du fondateur, il poursuit l'œuvre spirituelle et lance la construction de la grande mosquée, achevée pierre après pierre par les fidèles.", imageSeed: "cb-hist-issa" },
  { title: "Imam Mouhamadou Laye",      author: "Khalife · 1925 — 2000",    summary: "Sous son guide, Cambérène devient un foyer de transmission du tassou et accueille les pèlerinages annuels — la confrérie rayonne au-delà du Sénégal.", imageSeed: "cb-hist-mouhamadou" },
];

const TIMELINE_FALLBACK = [
  { title: "Fondation",            year: 1884, summary: "Seydina Limamou Laye se retire à Cambérène — naissance de la confrérie et du village.",       featured: true,  contributor: "1884" },
  { title: "La Grande Mosquée",    year: 1909, summary: "Édifiée par les fidèles, elle devient le cœur architectural et spirituel du village.",        featured: false, contributor: "1909 — 1949" },
  { title: "Pirogues & pêche",     year: 1965, summary: "Le débarquadère structure l'économie locale ; Cambérène devient un port artisanal majeur.",   featured: false, contributor: "1965" },
  { title: "Naissance de l'ASC",   year: 1986, summary: "Première équipe officielle du Navétane — le sable forme désormais les jambes.",                featured: false, contributor: "1986" },
  { title: "Cambérène, plateforme",year: 2024, summary: "La communauté ouvre ses archives et sa vie quotidienne au monde — c'est ici.",                featured: true,  contributor: "2024 →" },
];

const TRADITIONS_FALLBACK = [
  { title: "Le Tassou",          summary: "Chant religieux Layène, transmis de génération en génération. Une mémoire vivante portée par la voix.",       contributor: "♪" },
  { title: "Procession blanche", summary: "Tenue immaculée pour les célébrations majeures — symbole de pureté et d'unité de la communauté.",            contributor: "✦" },
  { title: "L'Appel",            summary: "Pèlerinage annuel (Niani) commémorant la révélation du Mahdi — des milliers convergent vers Cambérène.",     contributor: "⊙" },
  { title: "Les Pirogues",       summary: "L'ablution à l'aube, le départ en mer, le retour au crépuscule — un quotidien rythmé par l'Atlantique.",     contributor: "≈" },
];

const ARCHIVES_FALLBACK = [
  { title: "Mosquée · vue aérienne · années 1960",         imageSeed: "cb-arc-mosque-1960" },
  { title: "Procession blanche · Niani 1972",              imageSeed: "cb-arc-procession-1972" },
  { title: "Pirogues au mouillage · 1978",                 imageSeed: "cb-arc-pirogues-1978" },
  { title: "Marché aux poissons · années 1980",            imageSeed: "cb-arc-marche-1980" },
  { title: "Équipe ASC · saison inaugurale 1986",          imageSeed: "cb-arc-asc-1986" },
  { title: "Ablutions à l'aube · plage Nord",              imageSeed: "cb-arc-ablutions-aube" },
  { title: "Anciens du village · portrait collectif 1991", imageSeed: "cb-arc-anciens-1991" },
];

function archiveImage(a) {
  if (a.imageUrl) return a.imageUrl;
  const seed = a.imageSeed || a.slug || `cb-arc-${a.id || 'unknown'}`;
  return `https://picsum.photos/seed/${seed}/1400/1000`;
}

// Fetch helper : appel API + fallback automatique si l'API renvoie [] ou échoue
async function loadCollection({ collection, type, limit, fallback }) {
  try {
    const data = await archivesApi.list({ collection, type, limit });
    const items = data.items || [];
    return items.length > 0 ? items : fallback;
  } catch {
    return fallback;
  }
}

const Histoire = () => {
  const [figures,    setFigures]    = useState(FIGURES_FALLBACK);
  const [timeline,   setTimeline]   = useState(TIMELINE_FALLBACK);
  const [traditions, setTraditions] = useState(TRADITIONS_FALLBACK);
  const [archives,   setArchives]   = useState(
    ARCHIVES_FALLBACK.map((a, i) => ({ ...a, cls: TILE_SLOTS[i] }))
  );

  // Reveal animations (relancées quand les data changent)
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.histoire-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  }, [figures, timeline, traditions, archives]);

  // 4 fetchs parallèles vers l'API Archives (un par collection)
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      loadCollection({ collection: 'figures-fondatrices', limit: 6,  fallback: FIGURES_FALLBACK }),
      loadCollection({ collection: 'jalons-historiques',  limit: 10, fallback: TIMELINE_FALLBACK }),
      loadCollection({ collection: 'traditions-layenes',  limit: 8,  fallback: TRADITIONS_FALLBACK }),
      loadCollection({ type: 'photo',                     limit: 7,  fallback: ARCHIVES_FALLBACK }),
    ]).then(([figs, tl, tr, arc]) => {
      if (cancelled) return;
      setFigures(figs);
      setTimeline(tl);
      setTraditions(tr);
      // Compléter à 7 + attribuer les slots de tuile
      const filled = [...arc, ...ARCHIVES_FALLBACK].slice(0, 7);
      setArchives(filled.map((a, i) => ({ ...a, cls: TILE_SLOTS[i] })));
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="histoire-page">
      {/* ============== HERO ============== */}
      <header className="hist-hero">
        <div className="hist-hero-bg" />
        <div className="hist-hero-grain" />

        <div className="hist-hero-meta">
          <div><b>14.78°N</b><span>Cambérène · Côte Nord</span></div>
          <div><b>1884</b><span>Année de fondation</span></div>
        </div>

        <div className="wrap hist-hero-inner reveal">
          <span className="hist-hero-eyebrow">Récit · Layène · Depuis 1884</span>
          <h1>
            Une mémoire posée<br />
            face <span className="ital">à l'océan.</span>
          </h1>
          <p className="lede">
            Village layène posé sur la côte nord de Dakar, Cambérène raconte une histoire
            où le sacré et le quotidien s'enchâssent — pirogues à l'aube, processions blanches,
            chants tassou. Une mémoire transmise de voix en voix depuis sept générations.
          </p>

          <div className="hist-hero-stats">
            <div className="hist-hero-stat"><b>142</b><span>Années d'histoire</span></div>
            <div className="hist-hero-stat"><b>7</b><span>Générations</span></div>
            <div className="hist-hero-stat"><b>38&nbsp;400</b><span>Habitants aujourd'hui</span></div>
          </div>
        </div>

        <div className="hist-hero-scroll">
          <span>Faire défiler</span>
          <i />
        </div>
      </header>

      {/* ============== QUOTE ============== */}
      <section className="hist-quote reveal">
        <div className="wrap">
          <p>« Cambérène n'est pas un quartier de Dakar. C'est un village qui regarde l'océan en face. »</p>
          <span className="src">Recueilli auprès des anciens · Archives communautaires, 2024</span>
        </div>
      </section>

      {/* ============== FIGURES ============== */}
      <section className="hist-section hist-figures">
        <div className="wrap">
          <div className="hist-section-head reveal">
            <div>
              <span className="eyebrow">Les figures fondatrices</span>
              <h2>Trois <span className="ital">guides.</span></h2>
            </div>
            <p>
              De la révélation à la transmission contemporaine — les trois Khalifes
              qui ont façonné l'identité spirituelle et sociale de Cambérène.
            </p>
          </div>

          <div className="fig-grid">
            {figures.map((f, i) => (
              <article key={f.id || f.title} className="fig-card reveal">
                <div className="photo">
                  <div className="img" style={{ backgroundImage: `url('${archiveImage(f)}')` }} />
                  <div className="grad" />
                  <span className="years">
                    {String(i + 1).padStart(2, '0')}
                    {f.contributor ? ` · ${f.contributor}` : ''}
                  </span>
                </div>
                <div className="body">
                  <span className="role">{f.author || (f.year ? `Né en ${f.year}` : '')}</span>
                  <h3>{f.title}</h3>
                  <p>{f.summary}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TIMELINE ============== */}
      <section className="hist-section hist-timeline">
        <div className="wrap">
          <div className="hist-section-head reveal">
            <div>
              <span className="eyebrow">Frise · 1884 → 2026</span>
              <h2>Cinq moments<br /><span className="ital">clés.</span></h2>
            </div>
            <p>
              Cent quarante-deux années, condensées en cinq jalons qui ont
              façonné Cambérène — du fondateur au village numérique.
            </p>
          </div>

          <div className="tl">
            {timeline.map((t, i) => (
              <div key={t.id || `${t.year}-${i}`} className={`tl-item reveal${t.featured ? ' major' : ''}`}>
                <span className="year">{t.contributor || (t.year ? String(t.year) : '—')}</span>
                <h4>{t.title}</h4>
                <p>{t.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TRADITIONS ============== */}
      <section className="hist-section hist-traditions">
        <div className="wrap">
          <div className="hist-section-head reveal">
            <div>
              <span className="eyebrow">Pratiques fondatrices</span>
              <h2>Le quotidien<br /><span className="ital">comme prière.</span></h2>
            </div>
            <p>
              À Cambérène, le sacré n'est pas séparé du temps ordinaire — il s'infuse
              dans le travail des pêcheurs, les vêtements blancs et les chants à l'aube.
            </p>
          </div>

          <div className="trad-grid">
            {traditions.map((t, i) => (
              <article key={t.id || t.title} className="trad-card reveal">
                <span className="num">{String(i + 1).padStart(2, '0')}</span>
                <span className="trad-icon">{t.contributor || '◇'}</span>
                <h4>{t.title}</h4>
                <p>{t.summary}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== ARCHIVES ============== */}
      <section className="hist-section hist-archives">
        <div className="wrap">
          <div className="hist-section-head reveal">
            <div>
              <span className="eyebrow">Archives communautaires</span>
              <h2>Cambérène,<br /><span className="ital">en images.</span></h2>
            </div>
            <p>
              Clichés conservés par les habitants, restaurés et numérisés depuis 2024.
              Une fenêtre sur le village d'hier, ouverte sur celui d'aujourd'hui.
            </p>
          </div>

          <div className="arc-grid reveal">
            {archives.map((a) => (
              <div
                key={a.id || a.cls}
                className={`arc-tile ${a.cls}`}
                data-caption={a.title || a.caption}
                style={{ backgroundImage: `url('${archiveImage(a)}')` }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="hist-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Aller plus loin</span>
          <h3>L'histoire complète vous attend dans <span className="ital">la bibliothèque.</span></h3>
          <p>
            Textes fondateurs, enregistrements audio de tassou, archives photographiques
            et témoignages — accessibles à tous, contribués par la communauté.
          </p>
          <div className="hist-cta-actions">
            <Link to="/bibliotheque" className="btn btn-primary">
              <span>Explorer la bibliothèque</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/spiritualite" className="btn btn-outline">
              <span>Culture Layène</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Histoire;
